import { Component, EventEmitter, OnDestroy, OnInit, Output } from "@angular/core";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { TranslateService } from "@ngx-translate/core";
import { catchError, concatMap, map, of, Subject, Subscription, switchMap, takeUntil, timer, Observable } from "rxjs";
import { CommonVariable } from "src/app/app.constants";
import { SyndicateDataService } from "src/app/services/syndicate-data.service";
import { IonicModule } from "@ionic/angular";
import { AnimationOptions, LottieComponent } from "ngx-lottie";
import { AnimationService } from "src/app/services/animation.service";

@Component({
    selector: 'app-syndicate-previous-checks',
    templateUrl: './syndicate-previous-checks.component.html',
    styleUrl: './syndicate-previous-checks.component.scss',
    standalone: true,
    imports: [IonicModule, LottieComponent]
})
export class SyndicatePreviousChecksComponent implements OnInit, OnDestroy {
  selectedLanguage: string = CommonVariable.preferredLanguage;
  public spanish: boolean;
  @Output() showPreviousExpandViewEvent = new EventEmitter<void>();
  public syndicateData: Data;
  private syndicateDataSubscription: Subscription;
  private animationDestroy$ = new Subject<void>();
  updateAnimationSubject = new Subject<{ location_id: string, delayTime: number }>();
  public userId: string;
  previousCheckStatus: boolean;
  previousCheckDuration: number;
  previousCheckOptions: AnimationOptions = { path: '' };

  constructor(
    private authService: AuthService,
    public translate: TranslateService,
    private syndicateDataService: SyndicateDataService,
    private animationService : AnimationService,
  ) {
    this.authService?.selectedlanguage$?.subscribe(
      (l) => {
        this.selectedLanguage = l ? l : "en";
        translate.use(l ? l : "en");
        if (l == "es") {
          this.spanish = true;
        }
      },
      (err) => {
        return err;
      }
    );

    // To fetch data to bind on the template
    this.syndicateDataSubscription = this.syndicateDataService?.getSyndicatePageData()?.subscribe({
      next: (data) => {
        this.syndicateData = data;
      },
      error: (err) => {
        return err;
      },
    });
  }
  async ngOnInit() {
    await this.authService
      ?.getUid("SyndicatePreviousChecksComponent")
      ?.then((uid) => {
        this.userId = uid;
      })
      .catch((error) => {
        return error;
      });
    this.getAnimation();
    this.initUpdateAnimation();
  }

  getAnimation() {
    this.animationService.getAnimationFB(this.userId).pipe(
      map((response: Data) => response?.syndicate ?? []),
      takeUntil(this.animationDestroy$)
    ).subscribe((res: Data) => {
      res?.forEach(item => {
        switch (item?.location_id) {
          case 'previous_checks_widget':
            this.previousCheckStatus = item?.status;
            this.previousCheckDuration = item?.duration;
            this.previousCheckOptions = {
              path: `../../../assets/${item?.lottie_animation_URL}`,
            }
            break;
        }
      });
    })
  }

  initUpdateAnimation() {
    this.updateAnimationSubject
      .pipe(
        concatMap(({ location_id, delayTime }) =>
          timer(delayTime * 1000).pipe(
            switchMap(() => this.sendUpdateStatusFB(location_id) || of(null))
          )
        ),
        takeUntil(this.animationDestroy$)
      ).subscribe();
  }

  // Dismiss the animation when user clicks on it.
  updateAnimationStatus(location_id: string, delayTime: number) {
    if (location_id && delayTime > 0 && delayTime != null) {
      this.updateAnimationSubject.next({ location_id, delayTime });
    }
  }

  onClick(location_id: string) {
    this.sendUpdateStatusFB(location_id).subscribe();
  }

  sendUpdateStatusFB(location_id: string): Observable<Data> {
    // Ensure location_id is available, return empty Observable if not
    if (!location_id) {
      return of(null);
    }

    // Check if previous check animation is active
    if (this.previousCheckStatus) {
      return this.authService.getV2CloudFunction("updatemicroanimationv2", {
        "userId": this.userId,
        "locationIds": location_id,
        "status": false
      }).pipe(
        catchError((error) => {
          this.authService.errorHandlerLogs('Error in sendUpdateStatusFB cloud function:', error);
          return of(null);
        })
      );
    }

    // Return empty Observable instead of undefined
    return of(null);
  }

  showPreviousExpandView(event) {
    this.showPreviousExpandViewEvent.emit(event);
  }

  ngOnDestroy(): void {
    this.syndicateDataSubscription?.unsubscribe();
    if (this.animationDestroy$) {
      this.animationDestroy$.next();
      this.animationDestroy$.complete();
    }
  }
}
